import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { track } from '../../../util/analytics';
import { isWebsitePath } from '../../../util/marketingRoutes';

const DISMISSED_KEY = 'tc-shop-assessment-popup-dismissed-at';
const SHOWN_KEY = 'tc-shop-assessment-popup-shown';
const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;
const TIME_TRIGGER_MS = 18_000;
const SCROLL_TRIGGER_PCT = 0.35;

function wasRecentlyDismissed() {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_KEY));
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

export function ShopAssessmentPopup() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const pathname = router.asPath.split(/[?#]/)[0];
  const canShow =
    isWebsitePath(pathname) && pathname !== '/how-automated-is-your-shop';

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {}
    setIsOpen(false);
    track('shop_assessment_popup_dismissed', { page_path: pathname });
  }, [pathname]);

  useEffect(() => {
    setIsOpen(false);
    if (!canShow || typeof window === 'undefined') return;

    try {
      if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return;
    } catch {}
    if (wasRecentlyDismissed()) return;

    let fired = false;
    const show = () => {
      if (fired) return;
      fired = true;
      try {
        window.sessionStorage.setItem(SHOWN_KEY, '1');
      } catch {}
      setIsOpen(true);
      track('shop_assessment_popup_viewed', { page_path: pathname });
    };

    const timer = window.setTimeout(show, TIME_TRIGGER_MS);
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable >= SCROLL_TRIGGER_PCT) show();
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [canShow, pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dismiss, isOpen]);

  if (!canShow || !isOpen) return null;

  return (
    <aside
      className='tc-assessment-popup'
      role='dialog'
      aria-modal='false'
      aria-labelledby='tc-assessment-popup-title'
    >
      <button
        type='button'
        className='tc-assessment-popup__close'
        aria-label='Dismiss shop automation assessment'
        onClick={dismiss}
      >
        <svg aria-hidden='true' viewBox='0 0 24 24'>
          <path d='M6 6l12 12M18 6L6 18' />
        </svg>
      </button>
      <span className='tc-eyebrow'>Free 5-minute assessment</span>
      <h2 id='tc-assessment-popup-title'>How automated is your shop?</h2>
      <p>
        See where quoting, ordering, production, and fulfillment still rely on
        manual handoffs—and which improvement to tackle first.
      </p>
      <Link
        href='/how-automated-is-your-shop'
        className='tc-button tc-button--primary'
        onClick={() =>
          track('shop_assessment_popup_clicked', { page_path: pathname })
        }
      >
        Check my shop
      </Link>
      <small>14 practical questions. Get your score and next steps.</small>
    </aside>
  );
}

export default ShopAssessmentPopup;
